import React, { useRef, useEffect, useState } from "react";
import Transition from "../utils/Transition";
import { useSmallSize } from "../utils/UseSmallSize.jsx";

function ModalBasic({
  children,
  id,
  title,
  modalOpen,
  setModalOpen,
  width,
  hideCloseButton,
  className,
  zIndex,
  titleBarClassName,
}) {
  const modalContent = useRef(null);
  const isSmallSize = useSmallSize();
  const [shouldRender, setShouldRender] = useState(false);
  
  // Swipe down to close state
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);

  const widthClass = width ? "max-w-" + width : "";

  // Handle mounting/unmounting with proper transitions
  useEffect(() => {
    if (modalOpen) {
      setShouldRender(true);
    } else {
      // Delay unmounting to allow exit transition
      const timer = setTimeout(() => setShouldRender(false), 200);
      return () => clearTimeout(timer);
    }
  }, [modalOpen]);

  // Swipe down to close functionality for small screens
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    if (!isSmallSize) return;
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientY);
    setIsDragging(true);
    setDragOffset(0);
  };

  const onTouchMove = (e) => {
    if (!isSmallSize || !isDragging) return;
    const currentTouch = e.targetTouches[0].clientY;
    const diff = currentTouch - touchStart;
    
    // Only allow downward swipes
    if (diff > 0) {
      setDragOffset(diff);
      setTouchEnd(currentTouch);
    }
  };

  const onTouchEnd = () => {
    if (!isSmallSize || !isDragging) return;
    
    setIsDragging(false);
    
    if (!touchStart || !touchEnd) return;
    
    const distance = touchEnd - touchStart;
    const isDownSwipe = distance > minSwipeDistance;
    
    if (isDownSwipe) {
      setModalOpen(false);
    }
    
    // Reset drag offset
    setDragOffset(0);
    setTouchStart(null);
    setTouchEnd(null);
  };

  if (!shouldRender) {
    return null;
  }

  return (
    <>
      {/* Modal backdrop */}
      <Transition
        className="fixed inset-0 bg-slate-900 bg-opacity-30 z-50 transition-opacity"
        show={modalOpen}
        appear={true}
        enter="transition ease-out duration-200"
        enterStart="opacity-0"
        enterEnd="opacity-100"
        leave="transition ease-out duration-100"
        leaveStart="opacity-100"
        leaveEnd="opacity-0"
        aria-hidden="true"
        style={{ zIndex: zIndex || 50 }}
      />
      {/* Modal dialog */}
      <Transition
        id={id}
        className="fixed bottom-0 left-0 right-0 sm:inset-0 z-50 overflow-hidden flex items-center sm:my-4 justify-center px-0 sm:px-6"
        role="dialog"
        aria-modal="true"
        show={modalOpen}
        appear={true}
        enter="transition ease-in-out duration-200"
        enterStart="opacity-0 translate-y-4"
        enterEnd="opacity-100 translate-y-0"
        leave="transition ease-in-out duration-200"
        leaveStart="opacity-100 translate-y-0"
        leaveEnd="opacity-0 translate-y-4"
        style={{ zIndex: zIndex || 50 }}
      >
        <div
          ref={modalContent}
          className={
            "bg-white dark:bg-theme-surface-accent rounded shadow-lg overflow-hidden w-full flex flex-col min-h-0 " +
            // Constrain height to viewport to enable internal scrolling (avoid full 100vh on mobile)
            "max-h-[90vh] sm:max-h-[90vh] " +
            widthClass +
            (className ? " " + className : "")
          }
          style={{
            transform: isSmallSize && isDragging ? `translateY(${dragOffset}px)` : 'translateY(0)',
            transition: isDragging ? 'none' : 'transform 0.2s ease-out',
            // Safe area padding removed; max-height handles layout
          }}
        >
          {/* Sticky header area (drag handle + header) */}
          <div
            className="sticky top-0 z-[1] bg-white dark:bg-theme-surface-accent"
          >
            {/* Swipe indicator bar for small screens */}
            {isSmallSize && (
              <div 
                className="flex justify-center pt-2 pb-1"
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
              >
                <div className="w-8 h-1 bg-slate-300 dark:bg-slate-600 rounded-full"></div>
              </div>
            )}

            {/* Modal header */}
            <div 
              className={"px-5 py-3 border-b border-slate-200 dark:border-slate-700" + (titleBarClassName ? " " + titleBarClassName : "")}
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
            >
              <div className="flex justify-between items-center">
                <div className="font-semibold text-slate-800 dark:text-slate-100">
                  {title}
                </div>
                {!hideCloseButton && (
                  <button
                    className="text-slate-400 dark:text-slate-500 hover:text-slate-500 dark:hover:text-slate-400"
                    onClick={(e) => {
                      e.stopPropagation();
                      setModalOpen(false);
                    }}
                  >
                    <div className="sr-only">Close</div>
                    <svg className="w-4 h-4 fill-current">
                      <path d="M7.95 6.536l4.242-4.243a1 1 0 111.415 1.414L9.364 7.95l4.243 4.242a1 1 0 11-1.415 1.415L7.95 9.364l-4.243 4.243a1 1 0 01-1.414-1.415L6.536 7.95 2.293 3.707a1 1 0 011.414-1.414L7.95 6.536z" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div>
          {/* Scrollable body */}
          <div
            className={(isSmallSize ? "pt-4 px-2 pb-3" : "p-5") + " flex-1 overflow-y-auto min-h-0"}
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            {children}
          </div>
        </div>
      </Transition>
    </>
  );
}

export default ModalBasic;
